import { Activity, ActivityItem, Quiz, QuizFormData } from '../types';
import { buildQuizV2CreatePayload, QuizV2CreateRequestBody, QuizV2CreateSectionItem } from './buildQuizV2CreatePayload';
import { LinkedArticleRef, normalizeLinkedArticleVideo } from './normalizeLinkedArticleVideo';

/** GET /api/v2/admin/quiz/details/{uuid} (content after axios unwrap). */
export type AdminQuizDetailsMcq = {
    id: number;
    uuid: string;
    type: string;
    status: string;
    content: string;
    imageUrl: string | null;
    subType: string;
    options: {
        id: number;
        text: string;
        order: number;
        isCorrect: boolean;
        status: string;
    }[];
};

export type AdminQuizDetailsSectionItem = {
    id: number;
    type: string;
    itemId: number;
    order: number;
    question: AdminQuizDetailsMcq;
};

export type AdminQuizDetailsSection = {
    id: number;
    title: string;
    sectionType: string;
    order: number;
    article: unknown;
    items: AdminQuizDetailsSectionItem[];
};

export type AdminQuizDetailsContent = {
    quiz: {
        id: number;
        uuid: string;
        title: string;
        type: string;
        status: string;
        startTime: string | null;
        endTime: string | null;
        durationInMinutes: number | null;
        maxAttemptCount: number;
        evaluationType: string;
        accessType: string;
        creatorType?: string;
        sources?: unknown[];
    };
    sections: AdminQuizDetailsSection[];
    noOfQuestions?: number;
};

function isMcqSectionItem(
    it: AdminQuizDetailsSectionItem
): it is AdminQuizDetailsSectionItem & { question: AdminQuizDetailsMcq } {
    return it.type === 'QUESTION' && it.question?.type === 'MULTIPLE_CHOICE_QUESTION';
}

function buildArticleLinkedVideoItem(section: AdminQuizDetailsSection): ActivityItem | null {
    if (section.sectionType !== 'ARTICLE_LINKED_SECTION') {
        return null;
    }
    const raw = section.article;
    if (!raw || typeof raw !== 'object') {
        return null;
    }
    const unified = normalizeLinkedArticleVideo(raw as LinkedArticleRef, section.title);
    if (!unified) {
        return null;
    }

    return {
        originId: null,
        title: unified.title,
        image: null,
        type: 1,
        sortIndex: 1,
        isMultiple: false,
        rightAnswer: [],
        textOptions: [],
        article: {
            videoData: {
                articleId: unified.articleId,
                images: { large: unified.images.large, short: unified.images.short },
                title: unified.title,
                videoHash: unified.videoHash,
                videoUrl: unified.videoUrl,
                subtitles: unified.subtitles,
            },
        },
        isSelect: true,
    };
}

function mapSectionToActivityItems(section: AdminQuizDetailsSection): ActivityItem[] {
    const videoItem = buildArticleLinkedVideoItem(section);
    const mcqItems = [...section.items].filter(isMcqSectionItem).sort((a, b) => a.order - b.order);
    const orderOffset = videoItem ? 1 : 0;

    const questionItems: ActivityItem[] = mcqItems.map((it, idx) => {
        const q = it.question;
        const opts = [...q.options].sort((a, b) => a.order - b.order);
        const textOptions = opts.map((o, optIndex) => ({
            originId: optIndex + 1,
            text: o.text,
            sourceOptionId: o.id,
        }));
        const rightAnswer = opts
            .map((o, optIndex) => ({ isCorrect: o.isCorrect, originId: optIndex + 1 }))
            .filter((o) => o.isCorrect)
            .map((o) => o.originId);

        return {
            originId: it.id,
            title: q.content,
            image: q.imageUrl,
            type: 2,
            sortIndex: orderOffset + idx + 1,
            isMultiple: q.subType === 'MULTIPLE_CORRECT',
            rightAnswer,
            textOptions,
            article: null,
            isSelect: !videoItem && idx === 0,
            questionOrder: idx + 1,
        };
    });

    if (videoItem) {
        return [videoItem, ...questionItems];
    }
    return questionItems;
}

/** Maps quiz-svc admin quiz details to the shared `Quiz` shape used by FormQuiz. */
export function mapAdminQuizDetailsToQuiz(content: AdminQuizDetailsContent): Quiz {
    const { quiz, sections, noOfQuestions } = content;

    const sorted = [...sections].sort((a, b) => a.order - b.order);

    const activities: Activity[] = sorted.map((section, idx) => ({
        originId: section.id,
        title: section.title,
        sortIndex: idx + 1,
        isSelect: idx === 0,
        items: mapSectionToActivityItems(section),
    }));

    return {
        id: quiz.id,
        title: quiz.title,
        active: quiz.status === 'ACTIVE',
        startDate: quiz.startTime,
        endDate: quiz.endTime,
        duration: quiz.durationInMinutes ?? 0,
        countOfQuestions: noOfQuestions ?? 0,
        isGraded: quiz.evaluationType === 'GRADED',
        editable: true,
        createdAt: null,
        quizUuid: quiz.uuid,
        activities,
        restrictNonInstitutional: quiz.accessType === 'INSTITUTION',
    };
}

/** Same as {@link mapAdminQuizDetailsToQuiz} but for “copy as new quiz”: no server ids / uuid, title suffix. */
export function mapAdminQuizDetailsToQuizCopy(content: AdminQuizDetailsContent): Quiz {
    const mapped = mapAdminQuizDetailsToQuiz(content);
    const baseTitle = mapped.title?.trim() ?? '';

    return {
        ...mapped,
        id: 0,
        quizUuid: undefined,
        title: baseTitle,
    };
}

export type QuizV2UpdateRequestBody = QuizV2CreateRequestBody & {
    quiz: QuizV2CreateRequestBody['quiz'] & {
        id: number;
        uuid: string;
        status: string;
        creatorType?: string;
        sources?: unknown[];
    };
    sections: Array<
        QuizV2CreateRequestBody['sections'][number] & {
            id?: number;
            items: Array<
                QuizV2CreateSectionItem & {
                    id?: number;
                    question: QuizV2CreateSectionItem['question'] & {
                        id?: number;
                        uuid?: string;
                        status?: string;
                        options: Array<
                            QuizV2CreateSectionItem['question']['options'][number] & {
                                id?: number;
                                status?: string;
                            }
                        >;
                    };
                }
            >;
        }
    >;
};

export type BuildQuizV2UpdatePayloadOptions = {
    /** Map `restrictNonInstitutional` to quiz-svc `accessType` (OPEN vs INSTITUTION). */
    applyUserQuizAccessType?: boolean;
};

/**
 * Normalizes a create-shaped item (new question added during edit) into the update shape:
 * marks the question and all its options `ACTIVE` so quiz-svc's non-null status column is satisfied.
 */
function toNewUpdateItem(
    item: QuizV2CreateSectionItem
): QuizV2UpdateRequestBody['sections'][number]['items'][number] {
    const options = item.question.options.map((opt) => {
        const { sourceOptionId: _sourceOptionId, ...rest } = opt;
        return { ...rest, status: 'ACTIVE' as const };
    });

    return {
        ...item,
        question: {
            ...item.question,
            status: 'ACTIVE' as const,
            options,
        },
    };
}

/** Merges stable IDs from GET details into the create-shaped PUT body for quiz-svc. */
export function buildQuizV2UpdatePayload(
    values: QuizFormData,
    snapshot: AdminQuizDetailsContent,
    options?: BuildQuizV2UpdatePayloadOptions
): QuizV2UpdateRequestBody {
    const base = buildQuizV2CreatePayload(values);
    const q = snapshot.quiz;

    const quiz: QuizV2UpdateRequestBody['quiz'] = {
        ...base.quiz,
        id: q.id,
        uuid: q.uuid,
        status: q.status,
        maxAttemptCount: q.maxAttemptCount,
        sources: q.sources ?? [],
        ...(q.creatorType ? { creatorType: q.creatorType } : {}),
        ...(options?.applyUserQuizAccessType
            ? { accessType: values.restrictNonInstitutional ? 'INSTITUTION' : 'OPEN' }
            : {}),
    };

    const snapshotSections = [...snapshot.sections].sort((a, b) => a.order - b.order);

    const sections: QuizV2UpdateRequestBody['sections'] = base.sections.map((section, si) => {
        const snapSec = snapshotSections[si];
        if (!snapSec) {
            return { ...section, items: section.items.map(toNewUpdateItem) };
        }

        const snapMcqItems = [...snapSec.items].filter(isMcqSectionItem).sort((a, b) => a.order - b.order);

        const items = section.items.map((item, ii) => {
            const snapItem = snapMcqItems[ii];
            if (!snapItem) {
                return toNewUpdateItem(item);
            }
            const qn = snapItem.question;

            const options = item.question.options.map((opt) => {
                const { sourceOptionId, ...rest } = opt;
                return {
                    ...rest,
                    ...(typeof sourceOptionId === 'number' ? { id: sourceOptionId } : {}),
                    status: 'ACTIVE' as const,
                };
            });

            const question = {
                ...item.question,
                id: qn.id,
                uuid: qn.uuid,
                status: qn.status as 'ACTIVE' | 'DELETED',
                options,
            };

            return {
                ...item,
                id: snapItem.id,
                question,
            };
        });

        const articleRef =
            snapSec.sectionType === 'ARTICLE_LINKED_SECTION' &&
            snapSec.article &&
            typeof snapSec.article === 'object' &&
            'articleId' in snapSec.article
                ? { article: { articleId: (snapSec.article as { articleId: number }).articleId } }
                : {};

        return {
            ...section,
            id: snapSec.id,
            sectionType: snapSec.sectionType as 'DEFAULT' | 'ARTICLE_LINKED_SECTION',
            ...articleRef,
            items,
        };
    });

    return { quiz, sections };
}
